import { useEffect, useState } from 'react'
import { Minus, Plus } from 'lucide-react'

import { Button } from '~/elements/Button'
import { IconButton } from '~/elements/IconButton'
import { useCurrentWorkspacePlan, useSubscriptionMutation } from '~/features/billing/hooks/useBillingHooks'
import { formatCount, formatUsd, isPaidPlan } from '~/features/billing/utils'
import { ConfirmBillingDialog } from '~/components/billing/ConfirmBillingDialog'
import { cn } from '~/lib/utils'

const MAX_EXTRA = 1000

function Stepper({
  label,
  value,
  onChange,
  included,
  unitPrice
}: {
  label: string
  value: number
  onChange: (value: number) => void
  included: number | null
  unitPrice: string
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-4 py-3">
      <div className="flex flex-col">
        <span className="font-medium text-content">{label}</span>
        <span className="text-sm text-content3">
          {included == null ? 'Unlimited included' : `${formatCount(included)} included`} · {unitPrice} each
        </span>
      </div>
      <div className="flex items-center gap-2">
        <IconButton
          aria-label={`Remove one ${label.toLowerCase()}`}
          disabled={value <= 0}
          onClick={() => onChange(Math.max(0, value - 1))}
          size="small"
          variant="secondary"
        >
          <Minus />
        </IconButton>
        <input
          aria-label={label}
          className="h-9 w-16 rounded-md border bg-fill text-center font-mono text-sm"
          inputMode="numeric"
          min={0}
          onChange={(event) => {
            const next = Number.parseInt(event.target.value, 10)
            onChange(Number.isFinite(next) ? Math.min(Math.max(next, 0), MAX_EXTRA) : 0)
          }}
          type="number"
          value={value}
        />
        <IconButton
          aria-label={`Add one ${label.toLowerCase()}`}
          disabled={value >= MAX_EXTRA}
          onClick={() => onChange(Math.min(MAX_EXTRA, value + 1))}
          size="small"
          variant="secondary"
        >
          <Plus />
        </IconButton>
      </div>
    </div>
  )
}

export function AddonsCard({ className }: { className?: string }) {
  const { state, plan } = useCurrentWorkspacePlan()
  const subscription = useSubscriptionMutation()
  const [extraSeats, setExtraSeats] = useState(0)
  const [extraProjects, setExtraProjects] = useState(0)
  const [confirming, setConfirming] = useState(false)

  useEffect(() => {
    if (state) {
      setExtraSeats(state.quantities.extraSeats)
      setExtraProjects(state.quantities.extraProjects)
    }
  }, [state?.quantities.extraSeats, state?.quantities.extraProjects])

  if (!state || !plan?.addons || !isPaidPlan(state.plan) || !state.paddle?.subscriptionId) return null

  const interval = state.interval ?? 'month'
  const seatPrice = plan.addons.extraSeat[interval]
  const projectPrice = plan.addons.extraProject[interval]
  const dirty = extraSeats !== state.quantities.extraSeats || extraProjects !== state.quantities.extraProjects
  const total = extraSeats * seatPrice + extraProjects * projectPrice
  const delta =
    (extraSeats - state.quantities.extraSeats) * seatPrice +
    (extraProjects - state.quantities.extraProjects) * projectPrice

  return (
    <section className={cn('flex flex-col rounded-xl border bg-fill2 p-5', className)} id="billing-addons">
      <div className="mb-2 flex flex-col gap-1">
        <h3 className="font-semibold text-content">Extra seats and projects</h3>
        <p className="text-sm text-content2">
          Add capacity beyond what {plan.name} includes. Changes are prorated immediately.
        </p>
      </div>
      <div className="divide-y">
        <Stepper
          included={plan.limits?.seats ?? null}
          label="Extra seats"
          onChange={setExtraSeats}
          unitPrice={`${formatUsd(seatPrice)}/${interval}`}
          value={extraSeats}
        />
        <Stepper
          included={plan.limits?.projects ?? null}
          label="Extra projects"
          onChange={setExtraProjects}
          unitPrice={`${formatUsd(projectPrice)}/${interval}`}
          value={extraProjects}
        />
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <span className="text-sm text-content2">
          Add-ons: <span className="font-mono text-content">{formatUsd(total)}</span>/{interval}
        </span>
        <div className="flex gap-2">
          {dirty && (
            <Button
              onClick={() => {
                setExtraSeats(state.quantities.extraSeats)
                setExtraProjects(state.quantities.extraProjects)
              }}
              size="small"
              variant="secondary"
            >
              Reset
            </Button>
          )}
          <Button disabled={!dirty} onClick={() => setConfirming(true)} size="small" variant="primary">
            Update
          </Button>
        </div>
      </div>

      <ConfirmBillingDialog
        confirmLabel="Update subscription"
        description={
          <>
            <p>
              Your subscription will include {extraSeats} extra seat{extraSeats === 1 ? '' : 's'} and{' '}
              {extraProjects} extra project{extraProjects === 1 ? '' : 's'} ({formatUsd(total)}/{interval}).
            </p>
            <p>
              {delta >= 0 ?
                `The added ${formatUsd(delta)}/${interval} is prorated and charged now for the rest of the billing period.`
              : `The removed ${formatUsd(-delta)}/${interval} is prorated and credited for the rest of the billing period.`
              }
            </p>
          </>
        }
        loading={subscription.isPending}
        onConfirm={() => subscription.mutateAsync({ action: 'set_addons', extraSeats, extraProjects })}
        onOpenChange={setConfirming}
        open={confirming}
        title="Update extra seats and projects?"
      />
    </section>
  )
}
